import { Box, Card, CardContent, Collapse, TableCell, TableRow, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CellSelector from '../Cells/CellSelector';
import CellViewer from '../Cells/CellViewer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { createContext, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/AddBox';
import ToolTip from '@mui/material/Tooltip';
import { API, getTabColorHeaderMain, TrackerDataType } from '../../shared/constants/constants';
import { useLocation } from 'react-router-dom';
import { processCellsForAutoComplete } from '../../shared/functions/autoCompleteProcessing';
import AddLinkIcon from '@mui/icons-material/AddLink';
import UrlDialog from '../ServiceLog/UrlDialog';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { EngagementKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import EditCommsMaterialDialog from '../Cells/EditCommsMaterialDialog';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { useQueryClient } from '@tanstack/react-query';

const useStyles = makeStyles(({ palette }) => ({
  hiddenCellCard: {
    flex: 1,
    alignSelf: 'flex-start',
    margin: '0',
    textAlign: 'center',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
  },
  cardContent: {
    padding: '0 !important',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
  },
  cardName: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.whiteScaleDarker.main,
    padding: 8,
    color: palette.whiteScaleDarker.contrastText,
    fontWeight: 400,
  },
  cardData: {
    padding: 5,
    wordWrap: 'break-word',
  },
  hiddenColsContainer: {
    display: 'flex',
    width: '100%',
  },
  collapseWrapperInner: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    width: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  },
}));

const hiddenColsMap = {
  MediaInteraction: [
    {
      name: IssueKeys.dueDate,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: IssueKeys.contactMethod,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: IssueKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: IssueKeys.keyMessaging,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: IssueKeys.response,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: IssueKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: IssueKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
  ServiceLog: [
    {
      name: ServiceKeys.dueDate,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: ServiceKeys.keyMessaging,
      sx: {
        flex: '0 1 30%',
      },
    },
    {
      name: ServiceKeys.actions,
      sx: {
        flex: '0 1 30%',
      },
    },
    {
      name: ServiceKeys.linkedInteraction,
      sx: {
        flex: '0 1 15%',
      },
    },
    {
      name: ServiceKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
  EngagementLog: [
    {
      name: EngagementKeys.dueDate,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: EngagementKeys.contactMethod,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: EngagementKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: EngagementKeys.notes,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: EngagementKeys.background,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: EngagementKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: EngagementKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
};

const FileUploadButton = ({ file, onChange }) => {
  return (
    <>
      <input type="file" multiple id="file" ref={file} onChange={onChange} style={{ display: 'none' }} />
      <ToolTip title="add file(s)">
        <label htmlFor="file" data-cy="file-upload-button">
          <IconButton onClick={() => file.current.click()} size="large">
            <AddIcon style={{ color: 'white' }} />
          </IconButton>
        </label>
      </ToolTip>
    </>
  );
};

const UrlUploadButton = ({ setFormOpen }) => {
  return (
    <>
      <ToolTip title="add link to web-hosted file">
        <IconButton onClick={() => setFormOpen(true)} size="large" data-cy="url-upload-button">
          <AddLinkIcon style={{ color: 'white' }} />
        </IconButton>
      </ToolTip>
    </>
  );
};

export const FileProgressContext = createContext();

const TrackerCollapsedRow = ({
  cells,
  open,
  colSpan,
  dropdownTable,
  isEditMode,
  onEdit,
  type,
  linkedEventHandler,
  secondaryLinkedEventHandler,
  id,
}) => {
  const { pathname, state } = useLocation();
  const theme = useTheme();
  const classes = useStyles({ tabColor: getTabColorHeaderMain(pathname, theme) });
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const user = useSelector((state) => state.user);
  const userTenantHasEngagementAddOn = user.addonControls[AddOnTypes.engagementLog];

  const file = useRef(null);
  const [fileProgress, setFileProgress] = useState();

  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentFileToEdit, setCurrentFileToEdit] = useState(null);

  const [isDragActive, setDragActive] = useState(false);

  const invalidateFuzzySearch = () => {
    if (state?.queryString && state?.category) {
      return queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', type, state.queryString, state.category],
      });
    }
    return Promise.resolve();
  };

  const onChange = (colName) => (e) => {
    let invalidateQueryKey;
    if (type === TrackerDataType.SERVICE) {
      invalidateQueryKey = 'monthlyServices';
    } else if (type === TrackerDataType.ENGAGEMENT) {
      invalidateQueryKey = 'monthlyEngagements';
    } else {
      invalidateQueryKey = '';
    }

    if (e.target.files.length) {
      const headers = { 'content-type': 'multipart/form-data' };
      let files = new FormData();

      Object.values(e.target.files).forEach((file) => {
        files.append('file', file);
      });

      setFileProgress(0);

      API.post(`/${type === TrackerDataType.SERVICE ? 'service' : 'engagement'}/${id}/upload`, files, {
        headers: headers,
        onUploadProgress: (data) => {
          setFileProgress(Math.round((100 * data.loaded) / data.total));
        },
      })
        .then((response) => {
          if (response.status !== 201) {
            alert(response.statusText);
          } else {
            queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
            invalidateFuzzySearch();
            dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
          }

          const filesData = response.data.files || null;
          const cellData = cells[colName].concat(filesData);

          onEdit(cellData, colName);
          return response;
        })
        .catch((err) => {
          alert(`post failed - ${err}`);
        });
    }
  };

  const handleDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (colName) => (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.dataTransfer.files && e.dataTransfer.files[0] && isDragActive) {
      const headers = { 'content-type': 'multipart/form-data' };
      let files = new FormData();

      Object.values(e.dataTransfer.files).forEach((file) => {
        files.append('file', file);
      });

      setFileProgress(0);

      API.post(`/${type === TrackerDataType.ENGAGEMENT ? 'engagement' : 'service'}/${id}/upload`, files, {
        headers: headers,
        onUploadProgress: (data) => {
          setFileProgress(Math.round((100 * data.loaded) / data.total));
        },
      })
        .then((response) => {
          if (response.status !== 201) {
            alert(response.statusText);
          } else {
            invalidateFuzzySearch();
            dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
          }

          const filesData = response.data.files || null;
          const cellData = cells[colName].concat(filesData);

          onEdit(cellData, colName);
          return response;
        })
        .catch((err) => {
          alert(`post failed - ${err}`);
        });
    }
    setDragActive(false);
  };

  const handleSubmit = (callback, dialogValue, handleClose, colName, id, dispatch) => async (event) => {
    event.preventDefault();

    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      default:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
    }

    API.post(`/${pathPrefix}/${id}/url`, dialogValue)
      .then((response) => {
        if (response.status !== 201) {
          alert(response.statusText);
        } else {
          queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
          invalidateFuzzySearch();
          dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
        }
        return response;
      })
      .then((data) => {
        callback(data, colName);
      })
      .catch((e) => {
        console.log('post failed - ', e);
      });

    handleClose();
  };

  const handleCommsChange = (response, colName) => {
    const filesData =
      response.data.updatedService?.file ||
      response.data.updatedIssue?.file ||
      response.data.updatedEngagement?.file ||
      null;

    onEdit(filesData, colName);
  };

  const [openForm, setOpenForm] = useState(false);
  const [openSecondaryForm, setOpenSecondaryForm] = useState(false);

  const handleOpenEditModal = (file) => {
    setCurrentFileToEdit(file);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentFileToEdit(null);
  };

  const handleSaveCommsMaterialItem = async (updatedFile) => {
    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for saving file.' }));
        return;
    }

    try {
      const response = await API.put(`/${pathPrefix}/${id}/file/${updatedFile._id}`, {
        name: updatedFile.name,
        description: updatedFile.description,
        path: updatedFile.path,
      });

      if (response.status === 200) {
        await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        dispatch(openSnackbar({ severity: 'success', message: 'File details updated!' }));
        const updatedFiles =
          response.data.updatedService?.file ||
          response.data.updatedIssue?.file ||
          response.data.updatedEngagement?.file ||
          [];
        onEdit(
          updatedFiles,
          updatedFile.colName ||
            (type === TrackerDataType.SERVICE
              ? ServiceKeys.file
              : type === TrackerDataType.MEDIA_INTERACTION
              ? IssueKeys.file
              : EngagementKeys.file)
        );
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to update file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to update file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while updating the file.' }));
    }
  };

  const handleDeleteCommsMaterialItem = async (fileToDelete) => {
    let pathPrefix;
    let cellNameToUpdate;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        cellNameToUpdate = ServiceKeys.file;
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        cellNameToUpdate = IssueKeys.file;
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        cellNameToUpdate = EngagementKeys.file;
        invalidateQueryKey = 'monthlyEngagements';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for deleting file.' }));
        return;
    }

    try {
      const url = `/${pathPrefix}/${id}/${fileToDelete._id}/${encodeURIComponent(fileToDelete.name)}`;
      const response = await API.delete(encodeURI(url));

      if (response.status === 204) {
        await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        dispatch(openSnackbar({ severity: 'warning', message: `"${fileToDelete.name}" deleted!` }));
        const currentFiles = cells[cellNameToUpdate] || [];
        const updatedFiles = currentFiles.filter((f) => f._id !== fileToDelete._id);
        onEdit(updatedFiles, cellNameToUpdate);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to delete file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to delete file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while deleting the file.' }));
    }
  };

  const renderHiddenCell = ({ name, editSx, sx }) => {
    const hiddenCellCard = (
      <Card className={classes.hiddenCellCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.cardName}>
            {name === ServiceKeys.linkedInteraction && userTenantHasEngagementAddOn
              ? 'Interactions and Engagements'
              : name}
            {name === ServiceKeys.file || name === IssueKeys.file || name === EngagementKeys.file ? (
              <>
                <UrlDialog
                  colName={name}
                  type={type}
                  id={id}
                  isOpen={isUrlFormOpen}
                  setIsOpen={setIsUrlFormOpen}
                  callback={handleCommsChange}
                  handleSubmit={handleSubmit}
                />
                {type === TrackerDataType.SERVICE || type === TrackerDataType.ENGAGEMENT ? (
                  <FileUploadButton file={file} onChange={onChange(name)} />
                ) : (
                  <></>
                )}
                <UrlUploadButton setFormOpen={setIsUrlFormOpen} />
              </>
            ) : (
              <></>
            )}
          </Box>
          <Box style={{ padding: 6, minHeight: '5vh' }}>
            <FileProgressContext.Provider value={fileProgress}>
              {isEditMode ? (
                <CellSelector
                  cells={cells}
                  callBack={onEdit}
                  col={name}
                  dropdownTable={dropdownTable}
                  classes={classes}
                  id={id}
                  type={type}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop(name)}
                  isDragActive={isDragActive}
                  onEditItem={handleOpenEditModal}
                />
              ) : (
                <CellViewer
                  type={type}
                  cells={cells}
                  index={name}
                  callback={onEdit}
                  dropdownTable={dropdownTable}
                  openForm={() => setOpenForm(true)}
                  openSecondaryForm={() => setOpenSecondaryForm(true)}
                  classes={classes}
                  id={id}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop(name)}
                  isDragActive={isDragActive}
                  onEditItem={handleOpenEditModal}
                />
              )}
            </FileProgressContext.Provider>
          </Box>
        </CardContent>
      </Card>
    );

    // const wrapperSx = isEditMode ? editSx : sx;
    return (
      <Box key={`${type}-${name}`} sx={sx}>
        {hiddenCellCard}
      </Box>
    );
  };

  return (
    <>
      <TableRow style={{ display: open ? 'table-row' : 'none', padding: '4px' }}>
        <TableCell colSpan={colSpan} style={{ padding: '10px', paddingLeft: '20px', paddingRight: '15px' }}>
          <Collapse in={open} timeout={250} classes={{ wrapperInner: classes.collapseWrapperInner }}>
            <Box className={classes.hiddenColsContainer}>{hiddenColsMap[type].map((col) => renderHiddenCell(col))}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {linkedEventHandler && (
        <QueryAdderDrawer openForm={openForm} setOpenForm={setOpenForm}>
          <QueryAdder
            title={linkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenForm}
            eventHandler={linkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {secondaryLinkedEventHandler && (
        <QueryAdderDrawer openForm={openSecondaryForm} setOpenForm={setOpenSecondaryForm}>
          <QueryAdder
            title={secondaryLinkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenSecondaryForm}
            eventHandler={secondaryLinkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      <EditCommsMaterialDialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        fileData={currentFileToEdit}
        onSaveItem={handleSaveCommsMaterialItem}
        onDeleteItem={handleDeleteCommsMaterialItem}
        type={type}
      />
    </>
  );
};

export default TrackerCollapsedRow;
